// This is only for flex level paid by traveler
import { almostZero, roundNumber } from "@xola/ui-kit";
import {
    getPaymentLineItems,
    getRefundLineItems,
    getVoucherLineItems,
    isMembershipItem,
    isMistPurchase,
    isPartnerPurchase,
} from "common";
import { isEmpty, sumBy } from "lodash";
import {
    CouponType,
    LineItemObject,
    LineItemType,
    PaymentMethod,
    Purchase,
    PurchaseItem,
    SellerPaymentMethod,
} from "types";
import { almostEquals } from "../../../helpers/mathHelper";
import {
    getPaymentLineItemsWithoutRefunds,
    isExperiencePurchaseItem,
    isGift,
    isMultiplePaymentMethods,
    isStoreCredit,
} from "../../../models/Purchase";
import { RefundMethod } from "../../Event/CancelBookings/EventBookingsTable";
import { getRefundAmountToStoreCredit, getStoreCreditOperation } from "../TransferToStoreCreditModal";

export interface PayloadValues {
    comment: string;
    amount?: number; // Can be undefined if the user has not entered anything, is filtered out on the dryRun payload stage
    notify: boolean;
}

export type RefundPurchasePayloadType = {
    ops: any[];
    _emailOverrides: { sendEmailToTraveler: boolean };
};

export const getPayload = (
    paymentMethod: SellerPaymentMethod,
    purchase: Purchase,
    selectedItem: PurchaseItem | null,
    values: PayloadValues,
    shouldReduce: boolean,
    serviceFeeDelta = 0,
    isSubmit = false /* eslint-disable-next-line max-params */,
): RefundPurchasePayloadType => {
    const isStoreCreditRefund = paymentMethod?.id === PaymentMethod.STORE_CREDIT;
    const method = paymentMethod ?? getPurchasePaymentMethodForRefund(purchase, isStoreCreditRefund);
    const refundMethod = isStoreCreditRefund
        ? getPurchasePaymentMethodForRefund(purchase, isStoreCreditRefund)
        : method;

    // This will prevent API issues when amount is more than the max available, but form validation will not allow
    // submission until amount is correct
    // Rushi & Alexander TODO Check - https://xola01.atlassian.net/browse/X2-6483
    //                                https://github.com/xola/x2-seller/pull/2869

    const maxAmount = getRefundAmount(purchase, selectedItem, paymentMethod);
    const amountFormValue = Math.min(Math.abs(roundNumber(purchase.currency, values.amount ?? 0)), maxAmount);
    const amount = calculateAmount(purchase, amountFormValue, selectedItem, paymentMethod, shouldReduce);

    const item = selectedItem ?? purchase.items[0];
    const isGiftOrStoreCredit = isGift(item) || isStoreCredit(purchase);
    const itemAmount = item.amount < amount ? item.amount : amount;
    const isReducedToZero = almostZero(itemAmount);
    const decreaseAmount = itemAmount * -1;

    // Service fee delta is only valid for partial refunds, when doing a full refund the amount will include service fee
    const isPartialRefund = !almostEquals(amount, purchase.amount);

    const returnAmount =
        amount + (isPartialRefund && !isStoreCreditRefund && !almostZero(serviceFeeDelta) ? serviceFeeDelta : 0);
    const retainPriceStrategy = isMembershipItem(item) || isExperiencePurchaseItem(item);
    let ops: any[] = [
        {
            path: `/items/${item.id}/lineItems`,
            op: "addLineItem",
            data: {
                amount: decreaseAmount,
                name: values.comment || "Reduce booking value", // Send a default name if empty
                object: LineItemObject.DEFAULT,
                type: LineItemType.ADJUSTMENT,
                ...(retainPriceStrategy ? { _priceStrategy: "retain" } : null),
            },
        },
        {
            // The return payment
            path: "/lineItems",
            op: "addLineItem",
            data: {
                amount: returnAmount,
                name: "Return Payment",
                object: LineItemObject.REFUND,
                payload: LineItemType.REFUND,
                payment: { method: refundMethod?.id, ...(values.comment ? { comment: values.comment } : null) },
            },
        },
    ];

    if (isGiftOrStoreCredit) {
        ops[0].data.includeInAmount = true;
        ops[1].data.purchaseItem = { id: item.id };
    } else if (selectedItem) {
        ops[1].data.purchaseItem = { id: selectedItem.id };
    }

    if (method?.id === RefundMethod.STORE_CREDIT) {
        // When there are multiple payment modes and user want to refund the entire amount to store credit we need refund operations for each payment mode
        const isMultiplePaymentModes = isMultiplePaymentMethods(purchase, false);
        const refundLineItems = getRefundLineItems(purchase);
        const maxAmountForRefund = getRefundAmount(purchase, null, method);

        if (isMultiplePaymentModes && almostEquals(amount, maxAmountForRefund) && isEmpty(refundLineItems)) {
            ops.pop();
            const paymentLineItems = getPaymentLineItemsWithoutRefunds(purchase);
            const paymentLineItemsWithoutVoucher = paymentLineItems.filter(
                ({ coupon }) => coupon?.type !== CouponType.VOUCHER,
            );
            const refundLineItems = paymentLineItemsWithoutVoucher.map(({ amount, payment }) => {
                return getStoreCreditOperation(Math.abs(amount), payment?.method, values.comment);
            });
            ops.push(...refundLineItems);
        } // This is required to show partial refund payment summary correctly for dry run calls in case of store credit method selected
        else if (isMultiplePaymentModes && !isSubmit) {
            ops[1].data.refundToStoreCredit = true;
            ops[1].data.payment.method = PaymentMethod.STORE_CREDIT;
        } else {
            ops[1].data.refundToStoreCredit = true;
        }
    }

    if (refundMethod?.id === PaymentMethod.CREDIT_CARD) {
        ops[1].data.payment.card = refundMethod.card;
    }

    // Drop the reduce op when only returning payment, or when a normal item has nothing to reduce (X2-14239).
    if (!shouldReduce || (isReducedToZero && !isGiftOrStoreCredit)) {
        ops.shift();
    }

    if (isGiftOrStoreCredit) {
        // for gift and store credits, adding item lineItem and purchase lineitem should be using a single op addLineItemWithPayment.
        // This is to ensure any rollbacks on failed payments are handled properly.
        ops = updateOps(ops, item, decreaseAmount);
    }

    return {
        ops,
        _emailOverrides: { sendEmailToTraveler: values.notify },
    };
};

const updateOps = (ops: any[], item: PurchaseItem, decreaseAmount: number) => {
    if (ops[0].path.startsWith("/items/") && ops[1].path.startsWith("/lineItems")) {
        const updatedOps = [ops[1]];
        updatedOps[0].path = `/items/${item.id}/lineItems`;
        updatedOps[0].op = "addLineItemWithPayment";
        updatedOps[0].data.name = ops[0].data.name;
        updatedOps[0].data.amount = almostZero(decreaseAmount) ? 0 : decreaseAmount;
        updatedOps[0].data.object = LineItemObject.DEFAULT;
        updatedOps[0].data.type = LineItemType.ADJUSTMENT;

        return updatedOps;
    }

    return ops;
};

export const getFlexLevelServiceFeePaidByTraveler = (purchase: Purchase) => {
    return purchase.lineItems.find((item) => item.type === LineItemType.PARTNER_FEE && item.includeInAmount);
};

//
// This is refund amount, NOT the _return payment_ amount
//
export const getRefundAmount = (
    purchase: Purchase,
    item?: PurchaseItem | null,
    paymentMethod?: SellerPaymentMethod | null,
) => {
    if (isStoreCredit(purchase)) {
        return getRefundAmountToStoreCredit(purchase);
    }

    const isMist = isMistPurchase(purchase);

    // If an item is given, we should only refund the amount of that item (for MIST), otherwise we refund the total payment amount
    let purchaseRefundAmount = sumBy(getPaymentLineItems(purchase), "amount");

    // For partner packages, remove the amount of the partner item
    if (isPartnerPurchase(purchase)) {
        const partnerItems = purchase.lineItems.filter((item) =>
            [LineItemType.PARTNER_PACKAGE_ITEM, LineItemType.PARTNER_PURCHASE_ITEM].includes(item.type),
        );
        const partnerItemsTotal = partnerItems.reduce((sum, item) => {
            return sum + item.amount;
        }, 0);
        // Payment will be negative, and package item will be positive
        purchaseRefundAmount += partnerItemsTotal ?? 0;
    }

    // TODO: Can we calculate the max amount itself as `purchaseRefundAmount - serviceFee` and show to the user
    //              "Hey seller this is the max amount minus service fee" or whatever. Then our validation is easier
    const itemRefundAmount = item ? Math.max(-1 * item.amount, purchaseRefundAmount) : purchaseRefundAmount;
    let amount = isMist ? itemRefundAmount : purchaseRefundAmount;
    if (paymentMethod?.id === PaymentMethod.STORE_CREDIT && !isMist) {
        // If we are returning payment to store credit, we will not return the service fee
        // We use 'collected' when we have collected more service fee due to change in quantity
        const serviceFee = getFlexLevelServiceFeePaidByTraveler(purchase);
        const refundLineItems = getRefundLineItems(purchase);

        // We use 'collected' when we have collected more service fee due to change in quantity
        // Positive because `amount` is negative
        const amountWithServiceFeeCollected = serviceFee?.collected ?? serviceFee?.amount ?? 0;
        amount +=
            isEmpty(refundLineItems) || purchase.balance < 0 ? amountWithServiceFeeCollected : serviceFee?.amount ?? 0;
    }

    const voucherLineItems = getVoucherLineItems(purchase);
    if (!isEmpty(voucherLineItems)) {
        amount -= sumBy(voucherLineItems, "amount");
    }

    return roundNumber(purchase.currency, Math.abs(amount));
};

export const calculateAmount = (
    purchase: Purchase,
    amountFormValue: number,
    item: PurchaseItem | null,
    paymentMethod: SellerPaymentMethod | null,
    shouldReduce: boolean,
) => {
    // Adjust for flex level edge case when user enters the amount with fee
    const displayedMaxAmount = getRefundAmount(purchase, item, paymentMethod);
    if (isMistPurchase(purchase) || paymentMethod?.id === PaymentMethod.STORE_CREDIT) {
        return Math.min(displayedMaxAmount, amountFormValue);
    }

    // Get service fees that are included in the amount
    const serviceFee = getFlexLevelServiceFeePaidByTraveler(purchase);

    /**
     🚨IF YOU ARE WORKING ON REFUNDS READ THIS 🚨

     Problem:
     We display and allow users to enter a return amount that includes both the `item.amount` and the service fee.
     However, the reduced booking value should NOT include the fee. In instances where the user enters an amount
     inclusive of the service fee the dryRun API call will return an error.

     Solution:
     We adjust the amount on the UI to handle cases when the entered amount exceeds the amount WITHOUT the fee
     but remains less or equal to the amount WITH the fee.
     In such scenarios, we should process those amounts as the maximum possible value excluding the service fee.
     */
    const maxAmount = displayedMaxAmount - (serviceFee?.amount ?? 0);
    const isEnteredAmountWithFee = amountFormValue > maxAmount && amountFormValue <= displayedMaxAmount;

    if (isEnteredAmountWithFee && shouldReduce) {
        return maxAmount;
    }

    return amountFormValue;
};

export const getPurchasePaymentMethodForRefund = (
    purchase: Purchase,
    isStoreCreditRefund?: boolean,
): SellerPaymentMethod | null => {
    const lineItems = getPaymentLineItems(purchase);
    const lineItem = lineItems.find(
        (lineItem) =>
            lineItem.payment?.method &&
            lineItem.coupon?.type !== CouponType.VOUCHER &&
            lineItem.type !== LineItemType.AFFILIATE_DEPOSIT,
    );

    if (!lineItem?.payment?.method) {
        return null;
    }

    const { method, card } = lineItem.payment;

    return {
        id: method,
        name: method,
        comment: { title: "Comment", enabled: true, required: false },
        ...(method === PaymentMethod.CREDIT_CARD && card && !isStoreCreditRefund ? { card } : {}),
    };
};

export enum CardSelectionMode {
    AUTOMATIC_CARD_SELECTION = "Automatic card selection",
}
